<template>
  <div>
    <v-autocomplete
      v-model="build_id"
      :items="builds"
      :loading="loadingBuilds"
      label="Build"
      background-color="grey lighten-1"
      hide-details="auto"
      dense
      solo
      flat
      item-text="build_id"
      item-value="build_id"
      v-validate="{
        required: validation
      }"
      name="build id"
      :error-messages="errors.collect('build id')"
    />
    <div class="d-flex align-center">
      <v-autocomplete
        v-model="user_id"
        :items="users"
        :loading="loadingBuilds"
        label="User"
        class="pt-1"
        background-color="grey lighten-1"
        hide-details="auto"
        clearable
        dense
        solo
        flat
        item-text="user_name"
        item-value="user_id"
        v-validate="{
          required: validation
        }"
        name="user"
        :error-messages="errors.collect('user')"
      />
      <v-tooltip v-if="user_id" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            link
            :to="{
              path: '/telemetry/hardware-statistic',
              query: { map_id: mapId, build_id: build_id, user_id: user_id }
            }"
            target="_blank"
            icon
            small
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small>
              {{ mdiLaptop }}
            </v-icon>
          </v-btn>
        </template>
        <span>User hardware data</span>
      </v-tooltip>
    </div>
    <v-autocomplete
      v-model="session_id"
      :items="sessions"
      :loading="loadingSessions"
      label="Session"
      class="pt-1"
      background-color="grey lighten-2"
      hide-details="auto"
      clearable
      dense
      solo
      flat
      item-text="name"
      item-value="id"
      v-validate="{
        required: validation
      }"
      name="session id"
      :error-messages="errors.collect('session id')"
    >
      <template v-slot:item="data">
        <v-list-item-content>
          <v-list-item-title v-html="data.item.name"></v-list-item-title>
          <v-list-item-subtitle
            :class="{ 'red--text': !data.item.hasNewGame }"
            v-html="timeToString(data.item.time)"
          />
        </v-list-item-content>
      </template>
    </v-autocomplete>
    <template v-if="useNarrationNodes">
      <v-autocomplete
        v-model="start_narration_node"
        :items="startNarrationNodes"
        :loading="narrationNodesLoading"
        label="Start narration node"
        class="pt-1"
        background-color="grey lighten-3"
        clearable
        hide-details
        dense
        solo
        flat
        item-value="id"
        item-text="text"
      />
      <v-autocomplete
        v-if="finish_event_id === null"
        v-model="finish_narration_node"
        :items="finishNarrationNodes"
        :loading="narrationNodesLoading"
        label="Finish narration node"
        class="pt-1"
        background-color="grey lighten-3"
        clearable
        hide-details
        dense
        solo
        flat
        item-value="id"
        item-text="text"
      />
      <v-autocomplete
        v-else
        :value="finish_event_id"
        :items="finishEvents"
        :append-outer-icon="mdiDelete"
        label="Finish event"
        class="pt-1"
        background-color="grey lighten-4"
        hide-details
        dense
        solo
        flat
        item-value="id"
        item-text="text"
        @click:append-outer="updateFinishEvent(null)"
        @input="(event) => updateFinishEvent(event)"
      />
    </template>
    <template v-if="useSessionMinTime">
      <time-piker
        v-model="session_min_time"
        label="Minimal time for sessions"
        input-class="pt-1"
        background-color="grey lighten-5"
        hide-details
        dense
        solo
        flat
      />
    </template>
  </div>
</template>

<script>
import helper from '../../../plugins/helpers/mixin-helper'
import TimePiker from '../TimePickers/TimePiker.vue'
import { mdiDelete, mdiLaptop } from '@mdi/js'
export default {
  mixins: [
    helper
  ],
  components: {
    TimePiker
  },
  props: {
    mapId: {
      type: String,
      default () { return null }
    },
    filtersValue: {
      type: Object,
      default () {
        return {
          build_id: null,
          user_id: null,
          session_id: null,
          start_narration_node: null,
          finish_narration_node: null,
          session_min_time: '00:00:00'
        }
      }
    },
    validation: {
      type: Boolean,
      default () { return false }
    },
    useNarrationNodes: {
      type: Boolean,
      default () { return false }
    },
    useSessionMinTime: {
      type: Boolean,
      default () { return false }
    },
    updateNarrationNodesList: {
      type: Array,
      default () { return [] }
    }
  },
  data () {
    let finishEvents = []
    if (this.$route.query.finish && this.$route.query.finish !== 'null') {
      finishEvents = [{
        text: this.$route.query.finish[0],
        id: this.$route.query.finish[1]
      }]
    }

    return {
      mdiDelete,
      mdiLaptop,
      mounted: false,
      loadingBuilds: false,
      loadingSessions: false,
      narrationNodesLoading: false,
      builds: [],
      sessions: [],
      startNarrationNodes: [],
      finishNarrationNodes: [],
      finishEvents
    }
  },
  computed: {
    users () {
      const build = this.builds.find(build => build.build_id === this.build_id)

      if (build) {
        return build.users
      } else {
        return []
      }
    },
    build_id: {
      get () {
        return this.$store.state.telemetry.build_id
      },
      set (val) {
        this.$store.commit('telemetry/SET_BUILD_ID', val)
      }
    },
    user_id: {
      get () {
        return this.$store.state.telemetry.user_id
      },
      set (val) {
        this.$store.commit('telemetry/SET_USER_ID', val)
      }
    },
    session_id: {
      get () {
        return this.$store.state.telemetry.session_id
      },
      set (val) {
        this.$store.commit('telemetry/SET_SESSION_ID', val)
      }
    },
    start_narration_node: {
      get () {
        return this.$store.state.telemetry.start_narration_node
      },
      set (val) {
        this.$store.commit('telemetry/SET_START_NARRATION_NODE', val)
      }
    },
    finish_narration_node: {
      get () {
        return this.$store.state.telemetry.finish_narration_node
      },
      set (val) {
        this.$store.commit('telemetry/SET_FINISH_NARATION_NODE', val)
      }
    },
    finish_event_id: {
      get () {
        return this.$store.state.telemetry.finish_event_id
      },
      set (val) {
        this.$store.commit('telemetry/SET_FINISH_EVENT_ID', val)
      }
    },
    session_min_time: {
      get () {
        return this.$store.state.telemetry.session_min_time
      },
      set (val) {
        this.$store.commit('telemetry/SET_SESSION_MIN_TIME', val)
      }
    }
  },
  watch: {
    async mapId () {
      await this.getDataForFilters()
    },
    'filtersValue.build_id' (val) {
      this.build_id = val
    },
    'filtersValue.user_id' (val) {
      this.user_id = val
    },
    'filtersValue.session_id' (val) {
      this.session_id = val
    },
    'filtersValue.start_narration_node' (val) {
      this.start_narration_node = val
    },
    'filtersValue.finish_narration_node' (val) {
      this.finish_narration_node = val
    },
    'filtersValue.session_min_time' (val) {
      this.session_min_time = val
    },
    async build_id (val) {
      const build = this.builds.find(build => build.build_id === val)
      if (!build) return;

      if (build.users.some(user => user.id === this.user_id)) {
        this.session_id = null
        await this.getSessionsForFilters()
      } else {
        this.user_id = null
        this.session_id = null
        this.sessions = []
      }

      this.$emit('update', { build_id: val })

      if (this.updateNarrationNodesList.includes('build_id')) {
        this.start_narration_node = null
        this.finish_narration_node = null
        await this.getNarrationNodes()
      }
    },
    async user_id (val) {
      this.$emit('update', { user_id: val })
      await this.getSessionsForFilters()
      if (this.updateNarrationNodesList.includes('user_id')) {
        this.start_narration_node = null
        this.finish_narration_node = null
        await this.getNarrationNodes()
      }
    },
    async session_id (val) {
      this.$emit('update', { session_id: val })
      if (this.updateNarrationNodesList.includes('session_id')) {
        this.start_narration_node = null
        this.finish_narration_node = null
        await this.getNarrationNodes()
      }
    },
    start_narration_node (val) {
      this.$emit('update', { start_narration_node: val })
      const finishNarrationNode = this.finishNarrationNodes.find(node => node.id === val)
      if (finishNarrationNode && this.finish_narration_node === null) {
        this.finish_narration_node = val
      }
    },
    finish_narration_node (val) {
      this.$emit('update', { finish_narration_node: val })
    },
    finish_event_id (val) {
      this.$emit('update', { finish_event_id: val })
    },
    session_min_time (val) {
      this.$emit('update', { session_min_time: val })
    }
  },
  async mounted () {
    if (!this.mounted) {
      await this.getDataForFilters(this.user_id, this.session_id)
    }
    this.mounted = true
  },
  async activated () {
    if (this.mounted) {
      await this.getDataForFilters(this.user_id, this.session_id)
    }
  },
  methods: {
    async getDataForFilters (user_id = null, session_id = null) {
      this.user_id = user_id
      this.session_id = session_id
      this.sessions = []
      this.loadingBuilds = true
      const { data } = await this.$axios.get(
        this.$store.getters.projectPath + '/telemetry/data-for-filters/' + this.mapId
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.builds = data
        let build = this.builds.find(build => build.build_id === this.build_id)

        if (!build) {
          this.build_id = (data[0] || { build_id: null }).build_id
          build = data[0]
        }

        if (this.build_id) {
          if (build.users.some(user => user.user_id === this.user_id)) {
            await this.getSessionsForFilters()
          } else {
            this.user_id = null
            this.session_id = null
            this.sessions = []
          }
        }

        if (this.updateNarrationNodesList.includes('build_id')) {
          await this.getNarrationNodes()
        }
      }
      this.loadingBuilds = false
    },
    async getSessionsForFilters () {
      this.loadingSessions = true
      const { data } = await this.$axios.get(
        this.$store.getters.projectPath + '/telemetry/session-for-filters/' + this.build_id + '/' + this.user_id
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.sessions = data
        if (data.every(session => session.id !== this.session_id)) {
          this.session_id = null
        }
      }
      this.loadingSessions = false
    },
    async getNarrationNodes () {
      this.narrationNodesLoading = true
      const { data } = await this.$axios.get(this.$store.getters.projectPath + '/telemetry/narration-nodes', {
        params: {
          build_id: this.build_id,
          user_id: this.user_id,
          session_id: this.session_id
        }
      })
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.startNarrationNodes = data.start
        this.finishNarrationNodes = data.finish
      }
      this.narrationNodesLoading = false
    },
    async validate () {
      if (this.validation) {
        return this.$validator.validateAll({
          'build id': this.build_id,
          'user': this.user_id,
          'session id': this.session_id,
        })
      } else {
        return true
      }
    },
    updateFinishEvent (val) {
      if (val) {
        this.finish_narration_node = null
      } else {
        this.finish_event_id = null
        this.$emit('remove-finish')
      }
    }
  }
}
</script>